/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import {
    Icon,
    CustomValidation,
    Button,
    FlexRow,
    FlexCol,
    TextField,
    FocusOnMount,
} from 'sarsaparilla';

const propTypes = {
    index: PropTypes.number,
    removeLeader: PropTypes.func,
    leader: PropTypes.shape({
        first_name: PropTypes.string,
        email: PropTypes.string,
        cell_phone: PropTypes.string,
        last_name: PropTypes.string,
    }),
    className: PropTypes.string,
    alternateLeaderValidatorMethod: PropTypes.func.isRequired,
    isDisabled: PropTypes.bool,
    pActions: PropTypes.shape().isRequired,
    reservationId: PropTypes.string,
    paths: PropTypes.shape({
        email: PropTypes.string,
        last_name: PropTypes.string,
        first_name: PropTypes.string,
        alt_group_detail: PropTypes.string,
    }),
    name: PropTypes.string,
    addFieldRef: PropTypes.oneOfType([
        PropTypes.func,
        PropTypes.shape({ current: PropTypes.any }),
    ]),
};

function AlternatePermitHolderNameEmailEntry({
    isDisabled = false,
    className = '',
    paths = {
        email: 'email',
        last_name: 'last_name',
        first_name: 'first_name',
        alt_group_detail: 'alt_permit_holders',
    },
    index,
    removeLeader,
    pActions,
    reservationId,
    leader,
    name,
    addFieldRef,
    alternateLeaderValidatorMethod,
}) {
    const onClickRemove = () => {
        removeLeader(parseInt(index, 10));
    };

    const handleChange = (event) => {
        const input = event.target.value;
        const key = event.target.name;
        const path = `${paths.alt_group_detail}[${index}].${key}`;
        pActions.updatePendingReservation(reservationId, path, input);
    };

    const handleValidation = (key, isInvalid, error) => {
        const path = `${reservationId}_${paths.alt_group_detail}[${index}].${key}`;
        if (isInvalid) {
            pActions.formValidationChange(path, error);
        } else {
            pActions.formValidationChange(path, null);
        }
    };

    const nameValidatorMethod = (value) => {
        return isEmpty(value);
    };

    const renderRemoveLeaderButton = () => {
        if (isDisabled) {
            return <div />;
        }

        return (
            <div className="alternate-group-leader">
                <Button
                    type="button"
                    appearance="subtle-danger"
                    onClick={onClickRemove}
                    iconBeforeElement={<Icon iconName="delete" />}
                    screenReaderTextBefore="Remove"
                />
            </div>
        );
    };

    return (
        <div className="permit-alt-input-wrap m-1">
            <div className="permit-alt-header-wrap">
                <div className="permit-alt-header">
                    <FocusOnMount>Alternate Permit Holder #{index + 1}</FocusOnMount>
                </div>
                {renderRemoveLeaderButton()}
            </div>

            <FlexRow className="mb-1">
                <FlexCol xs={12} md={6} lg={4}>
                    <TextField
                        ref={addFieldRef?.(name, 0)}
                        id="other-first_name"
                        type="text"
                        name={paths.first_name}
                        label="Alternate First Name"
                        className={className}
                        value={get(leader, paths.first_name, '')}
                        onChange={handleChange}
                        readOnly={isDisabled}
                        disabled={isDisabled}
                        isRequired
                    />
                    <CustomValidation
                        value={get(leader, paths.first_name, '')}
                        displayInSummary
                        customValidator={nameValidatorMethod}
                        onValidated={() => handleValidation(paths.first_name)}
                        controlLabel="Alternate Permit Holder First Name"
                    />
                </FlexCol>
                <FlexCol xs={12} md={6} lg={4}>
                    <TextField
                        ref={addFieldRef?.(name, 1)}
                        id="other-last_name"
                        type="text"
                        name={paths.last_name}
                        label="Alternate Last Name"
                        className={className}
                        value={get(leader, paths.last_name, '')}
                        onChange={handleChange}
                        readOnly={isDisabled}
                        disabled={isDisabled}
                        isRequired
                    />
                    <CustomValidation
                        value={get(leader, paths.last_name, '')}
                        displayInSummary
                        customValidator={nameValidatorMethod}
                        onValidated={() => handleValidation(paths.last_name)}
                        controlLabel="Alternate Permit Holder Last Name"
                    />
                </FlexCol>
                <FlexCol xs={12} md={12} lg={4}>
                    <TextField
                        ref={addFieldRef?.(name, 2)}
                        id="other-email"
                        type="text"
                        name={paths.email}
                        label="Alternate Email Address"
                        className={className}
                        value={get(leader, paths.email, '')}
                        onChange={handleChange}
                        readOnly={isDisabled}
                        disabled={isDisabled}
                        isRequired
                    />
                    <CustomValidation
                        value={get(leader, paths.email, '')}
                        displayInSummary
                        customValidator={alternateLeaderValidatorMethod}
                        onValidated={() => handleValidation(paths.email)}
                        controlLabel="Alternate Permit Holder Email Address"
                    />
                </FlexCol>
            </FlexRow>
        </div>
    );
}

AlternatePermitHolderNameEmailEntry.propTypes = propTypes;

export default AlternatePermitHolderNameEmailEntry;
